import React, { useCallback, useEffect } from "react";
import "./button.scss";
import classNames from "classnames";
import { handleEnterKeypress } from "../../utils/keyEvents";
import { withDefaults } from "../../utils/withDefaults";

export const buttonStyles = {
  STANDARD_BUTTON: "STANDARD_BUTTON",
  OPAQUE_BUTTON: "OPAQUE_BUTTON",
  SECONDARY_BUTTON: "SECONDARY_BUTTON"
} as const;

export type ButtonStyle = (typeof buttonStyles)[keyof typeof buttonStyles];

/**
 * A button can be activated by a click, by Enter on the button itself, by a
 * form submit, or — when `shouldHandleGlobalEnterKeypress` is set — by Enter
 * anywhere on the page (a plain DOM event). Handlers see any of those.
 */
export type ButtonActivationEvent = React.SyntheticEvent | KeyboardEvent;

export interface ButtonProps {
  onClick?: (event: ButtonActivationEvent) => void;
  buttonStyle?: ButtonStyle;
  reference?: React.Ref<HTMLButtonElement>;
  isInDarkBackground?: boolean;
  type?: "button" | "submit" | "reset";
  className?: string;
  usePermanentAccent?: boolean;
  shouldHandleGlobalEnterKeypress?: boolean;
  onKeyPress?: (event: ButtonActivationEvent) => void;
  children?: React.ReactNode;
}

export default function Button(incomingProps: ButtonProps) {
  const props = withDefaults(incomingProps, buttonDefaultProps);

  const shouldShowAnimation =
    props.buttonStyle === buttonStyles.STANDARD_BUTTON;

  const getButtonClass = () => {
    switch (props.buttonStyle) {
      case buttonStyles.OPAQUE_BUTTON:
        return "opaque-button";
      case buttonStyles.SECONDARY_BUTTON:
        return "secondary-button";
      case buttonStyles.STANDARD_BUTTON:
      default:
        return "standard-button";
    }
  };

  const getButtonContextClass = () =>
    props.isInDarkBackground ? "dark-background" : "light-background";

  const { onClick, onKeyPress } = props;

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement> | KeyboardEvent) => {
      handleEnterKeypress(event, () =>
        onKeyPress === undefined ? onClick(event) : onKeyPress(event)
      );
    },
    [onClick, onKeyPress]
  );

  useEffect(() => {
    if (props.shouldHandleGlobalEnterKeypress) {
      document.addEventListener("keydown", handleKeyPress, false);
    }

    return () => document.removeEventListener("keydown", handleKeyPress, false);
  }, [props.shouldHandleGlobalEnterKeypress, handleKeyPress]);

  return (
    <button
      onClick={props.onClick}
      className={classNames(
        props.className,
        getButtonClass(),
        getButtonContextClass(),
        props.usePermanentAccent ? "use-permanent-accent" : ""
      )}
      type={props.type}
      ref={props.reference}
      onKeyPress={handleKeyPress}
    >
      {shouldShowAnimation ? <span /> : null}
      {props.children}
    </button>
  );
}

const buttonDefaultProps = {
  onClick: () => {},
  buttonStyle: buttonStyles.STANDARD_BUTTON,
  isInDarkBackground: false,
  type: "button" as const,
  className: "",
  usePermanentAccent: false,
  shouldHandleGlobalEnterKeypress: false
};
